import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Link2, ExternalLink, ShieldCheck, ArrowRight, AlertTriangle, Check } from 'lucide-react';

const RIOT_LOGOUT = 'https://login.riotgames.com/end-session-redirect?redirect_uri=https%3A%2F%2Fauth.riotgames.com%2Flogout';
const RIOT_LOGIN = 'https://auth.riotgames.com/authorize?redirect_uri=https%3A%2F%2Fplayvalorant.com%2Fopt_in&client_id=play-valorant-web-prod&response_type=token%20id_token&nonce=1&scope=account%20openid';

export default function AddAccount() {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { addAccount } = useAuth();
  const navigate = useNavigate();

  const validate = (u) => u.includes('access_token=') && u.includes('id_token=');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate(url)) {
      setError('Invalid Access URL. Make sure it contains access_token and id_token.');
      return;
    }
    setLoading(true);
    try {
      await addAccount(url);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to add account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">// add account // access url method</div>
          <h1 className="font-mono-display text-3xl text-white">Link a Valorant Account</h1>
          <p className="text-zinc-400 mt-2">Use the secure Access URL method to bind your Riot account without sharing credentials.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 neon-card rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-cyan-300"/>
              <h3 className="font-mono-display text-lg text-white">Access URL</h3>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-orange-400/70 rounded px-3 py-2">
                <Link2 className="w-4 h-4 text-zinc-500"/>
                <input value={url} onChange={(e)=>setUrl(e.target.value)} placeholder="https://playvalorant.com/en-gb/opt_in/#access_token=..." className="bg-transparent flex-1 outline-none text-white placeholder:text-zinc-600 text-sm"/>
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3">
                  <AlertTriangle className="w-4 h-4 mt-0.5"/>{error}
                </div>
              )}
              {success && (
                <div className="flex items-start gap-2 text-sm text-cyan-200 bg-cyan-400/10 border border-cyan-400/30 rounded p-3">
                  <Check className="w-4 h-4 mt-0.5"/>Account linked successfully! Fetched live from Riot. Redirecting...
                </div>
              )}
              <div className="flex items-center gap-3">
                <button disabled={loading} className="btn-neon">{loading ? 'Linking account...' : (<>Link Account <ArrowRight className="w-4 h-4"/></>)}</button>
                <Link to="/dashboard" className="btn-ghost">Cancel</Link>
              </div>
            </form>
          </div>

          <div className="lg:col-span-2 neon-card rounded-lg p-6">
            <div className="text-xs uppercase tracking-widest text-orange-300 mb-3">How to Obtain</div>
            <ol className="space-y-4">
              <li>
                <div className="font-mono-display text-white">1. Log out of Riot Games</div>
                <a href={RIOT_LOGOUT} target="_blank" rel="noreferrer" className="text-sm text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">Log out <ExternalLink className="w-3 h-3"/></a>
              </li>
              <li>
                <div className="font-mono-display text-white">2. Sign in to Riot Games</div>
                <a href={RIOT_LOGIN} target="_blank" rel="noreferrer" className="text-sm text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">Open Riot Sign-In <ExternalLink className="w-3 h-3"/></a>
              </li>
              <li>
                <div className="font-mono-display text-white">3. Copy the URL</div>
                <p className="text-xs text-zinc-400">After login you will be redirected to a 404 page. Copy the full address bar URL and paste it here.</p>
              </li>
            </ol>
            <div className="mt-4 text-[11px] text-zinc-500 border-t border-zinc-800 pt-3">Tip: Use Incognito / Guest mode to skip logging out.</div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
